import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, Shield, AlertTriangle, CheckCircle2, XCircle, ExternalLink, Phone, MapPin, Users, Truck } from "lucide-react";
import HomeScreenPrompt from "../components/HomeScreenPrompt";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * BrokerCheckPage — free broker/carrier verification tool.
 *
 * Route: /broker-check
 *
 * Uses existing POST /api/fmcsa/verify endpoint (built for signup verification)
 * to pull real FMCSA Company Census data. Adds a VERIFICATION verdict layer on
 * top so small carriers can instantly see if a broker is safe to work with.
 *
 * Positioning: this is a FREE, no-signup lead-gen tool that reinforces the
 * FreightPilotAI brand. Every carrier who uses it discovers our
 * paid product organically at the bottom of the results.
 */
export default function BrokerCheckPage({ embedded = false }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q");
    if (q) { setQuery(q); runCheck(null, q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runCheck = async (e, override) => {
    e?.preventDefault();
    const q = (override ?? query).trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setResult(null);

    const upper = q.toUpperCase();
    const hasMcPrefix = /^(MC|MX|FF)[-\s]?\d+$/.test(upper);
    const hasDotPrefix = /^(USDOT|DOT)[-\s]?\d+$/.test(upper);

    // Attempt strategy:
    //   - Explicit MC/MX/FF prefix → MC only
    //   - Explicit DOT prefix       → DOT only
    //   - Plain number              → try DOT first, fall back to MC if not found
    const attempts = [];
    if (hasMcPrefix) {
      attempts.push({ mc_number: q });
    } else if (hasDotPrefix) {
      attempts.push({ dot_number: q });
    } else {
      attempts.push({ dot_number: q });
      attempts.push({ mc_number: q });
    }

    try {
      let lastData = null;
      for (const payload of attempts) {
        const { data } = await axios.post(`${API_BASE}/api/fmcsa/verify`, payload);
        lastData = data;
        if (data?.verified) break;
      }
      setResult(lastData);
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Too many checks in a short time — take a breather and try again in an hour.");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Something went wrong. Try again with just the number (e.g. '1322149' or 'MC-1322149').");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? "text-white pb-8" : "min-h-screen bg-neutral-950 text-white"} data-testid={embedded ? "dot-check-embedded" : undefined}>
      {/* Sticky top nav */}
      {!embedded && (
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="broker-check-home-link">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-white/70 hover:text-white transition-colors"
            data-testid="broker-check-back-link"
          >
            ← Back to home
          </Link>
        </div>
      </div>
      )}

      {/* Hero + search */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #FF5A1F 0%, transparent 40%), radial-gradient(circle at 80% 70%, #FF5A1F 0%, transparent 40%)",
          }}
        />
        <div className={`relative max-w-4xl mx-auto px-6 text-center ${embedded ? "pt-4 pb-8" : "pt-16 pb-12"}`}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <Shield className="w-3.5 h-3.5" />
            {embedded ? "DOT Check · Live FMCSA data" : "Free · No signup · Broker verification tool"}
          </div>
          <h1 className={`font-black leading-[1.02] tracking-tight mb-5 ${embedded ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"}`}>
            Check any broker or carrier
            <br />
            <span className="text-[#FF5A1F]">before</span> you haul.
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
            Type any MC# or USDOT# below. We pull live FMCSA data and tell you if the operator is real, active, and safe to book with. Nothing to sign up for. This is what confident broker verification looks like.
          </p>

          {/* Search box */}
          <form onSubmit={runCheck} className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter MC# or DOT# (e.g., MC-1322149 or 3738067)"
                className="w-full bg-white/5 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-xl px-5 py-4 pl-12 text-lg text-white placeholder-white/40 outline-none transition-colors"
                autoFocus
                data-testid="broker-check-input"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <button
                type="submit"
                disabled={loading || !query.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-2.5 text-sm transition-colors"
                data-testid="broker-check-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking...
                  </span>
                ) : (
                  "Check now"
                )}
              </button>
            </div>
            <div className="text-[11px] text-white/40 mt-3">
              Try: <button type="button" onClick={() => setQuery("3738067")} className="underline hover:text-white/80" data-testid="broker-check-example-1">3738067</button>{" "}
              (Yaga Empire) · <button type="button" onClick={() => setQuery("MC-825275")} className="underline hover:text-white/80" data-testid="broker-check-example-2">MC-825275</button>{" "}
              (Altex Logistics)
            </div>
          </form>
        </div>
      </section>

      {/* Results section */}
      {(error || result) && (
        <section className="max-w-4xl mx-auto px-6 pb-16">
          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-6 text-center" data-testid="broker-check-error">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {result && !result.verified && (
            <div className="rounded-xl border-2 border-amber-500/60 bg-amber-500/10 p-8 text-center" data-testid="broker-check-not-found">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Verdict</div>
              <h2 className="text-2xl font-black mb-2 text-amber-100">Not found in FMCSA records</h2>
              <p className="text-amber-200/80 text-sm mb-4">
                {result.reason || "No matching carrier or broker in the FMCSA public database."}
              </p>
              <div className="text-left max-w-md mx-auto bg-black/30 rounded-lg p-4 text-sm">
                <div className="font-bold text-amber-100 mb-2">🚨 What this could mean:</div>
                <ul className="text-amber-200/80 space-y-1 text-[13px]">
                  <li>· Typo in the MC#/DOT# — double-check the number</li>
                  <li>· The operator is unregistered (illegal to haul interstate)</li>
                  <li>· A fake number used to look legitimate</li>
                  <li>· Very new carrier not yet in the census file</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-amber-500/20 text-amber-200 font-semibold">
                  <b>Do NOT accept a load from an unverifiable operator.</b> Call FMCSA at 1-800-832-5660 to confirm before booking.
                </div>
              </div>
            </div>
          )}

          {result && result.verified && (
            <div className="space-y-6" data-testid="broker-check-result">
              <VerdictCard result={result} />
              <DetailsGrid result={result} />
              <SafetyChecklist result={result} />
            </div>
          )}
        </section>
      )}

      {/* Educational section — public page only */}
      {!embedded && (
      <section className="border-t border-white/10 bg-neutral-900/50">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3 text-center">
            Why we built this
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 tracking-tight">
            Every year, $500M is lost to unverified brokers — most of it starts with fake broker MC numbers.
          </h2>
          <p className="text-white/70 text-center max-w-3xl mx-auto mb-12 leading-relaxed">
            Every day, small carriers accept loads from brokers who don't exist, get double-brokered by fake operators, or run for a "shipper" that vanishes at delivery. FMCSA data catches most of it in 5 seconds — but nobody's ever made checking the data easy. Now it is.
          </p>

          <div className="grid md:grid-cols-3 gap-5">
            <BenefitCard
              icon={<Shield className="w-6 h-6" />}
              title="Instant FMCSA lookup"
              body="Live data from the U.S. DOT public census file. Same info FMCSA gives you — no forms, no 3-day wait."
            />
            <BenefitCard
              icon={<AlertTriangle className="w-6 h-6" />}
              title="Instant verification verdict"
              body="Not just raw data — we tell you SAFE, CAUTION, or AVOID based on operating status, docket status, and OOS flags."
            />
            <BenefitCard
              icon={<CheckCircle2 className="w-6 h-6" />}
              title="Free, always"
              body="No account. No credit card. No 'unlock full report'. This is a tool, not a funnel."
            />
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 rounded-2xl border-2 border-[#FF5A1F]/40 bg-gradient-to-br from-[#FF5A1F]/20 to-transparent p-8 text-center">
            <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
              Want the full toolkit?
            </div>
            <h3 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">
              FreightPilotAI is the complete operating system for small carriers.
            </h3>
            <p className="text-white/70 max-w-xl mx-auto mb-6">
              BrokerCheck is 1 tool of 6 we're building. 3 more are live (RateCheck, Broker Directory, Careers Page Builder). Chrome load-board filter + docs vault coming next. All designed for carriers under 20 trucks.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-6 py-3 transition-colors"
              data-testid="broker-check-cta-signup"
            >
              Set It Up For Me — Free
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Footer */}
      {!embedded && (
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>
            © 2026 <b className="text-white">FreightPilotAI</b> · Data sourced from FMCSA public records
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
      )}
      {!embedded && <HomeScreenPrompt source="brokercheck" when={!!result} delayMs={2500} />}
    </div>
  );
}

/* ═════════════════════════ VERDICT CARD ═════════════════════════ */
function VerdictCard({ result }) {
  const isActive = result.status_code === "A";
  const docketActive = result.docket_status === "A";
  const oos = result.out_of_service;
  const isForHire = String(result.entity_type || "").toUpperCase().includes("AUTHORIZED");

  // Compute overall verdict
  let verdict, verdictColor, verdictBg, verdictBorder, verdictIcon, verdictMsg;
  if (oos || !isActive) {
    verdict = "AVOID";
    verdictColor = "text-red-100";
    verdictBg = "bg-red-500/15";
    verdictBorder = "border-red-500/60";
    verdictIcon = <XCircle className="w-14 h-14 text-red-400" />;
    verdictMsg = "This operator is INACTIVE or OUT OF SERVICE. Do not book loads with them. Their authority is suspended or revoked by FMCSA.";
  } else if (!docketActive && result.docket_number) {
    verdict = "CAUTION";
    verdictColor = "text-amber-100";
    verdictBg = "bg-amber-500/15";
    verdictBorder = "border-amber-500/60";
    verdictIcon = <AlertTriangle className="w-14 h-14 text-amber-400" />;
    verdictMsg = "Carrier is active but their MC docket status shows non-active. Verify insurance + authority before working with them.";
  } else if (!isForHire) {
    verdict = "CAUTION";
    verdictColor = "text-amber-100";
    verdictBg = "bg-amber-500/15";
    verdictBorder = "border-amber-500/60";
    verdictIcon = <AlertTriangle className="w-14 h-14 text-amber-400" />;
    verdictMsg = "This entity is NOT registered as 'Authorized For Hire.' They may be a private carrier hauling their own goods, not a broker/carrier you should contract with.";
  } else {
    verdict = "SAFE TO CHECK FURTHER";
    verdictColor = "text-emerald-100";
    verdictBg = "bg-emerald-500/15";
    verdictBorder = "border-emerald-500/60";
    verdictIcon = <CheckCircle2 className="w-14 h-14 text-emerald-400" />;
    verdictMsg = "Active, authorized for-hire operator with a valid MC docket. Still verify their insurance certificate and payment history (Ansonia, RMIS) before hauling.";
  }

  return (
    <div className={`rounded-2xl border-2 ${verdictBorder} ${verdictBg} p-8`}>
      <div className="flex items-start gap-6">
        <div className="shrink-0">{verdictIcon}</div>
        <div className="flex-1">
          <div className={`text-[11px] uppercase tracking-widest font-bold mb-1 ${verdictColor} opacity-70`}>
            Verdict
          </div>
          <h2 className={`text-3xl sm:text-4xl font-black mb-2 ${verdictColor} tracking-tight`}>
            {verdict}
          </h2>
          <div className={`text-lg font-bold mb-3 ${verdictColor}`}>
            {result.legal_name}
          </div>
          <p className={`${verdictColor} opacity-80 leading-snug`}>{verdictMsg}</p>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════ DETAILS GRID ═════════════════════════ */
function DetailsGrid({ result }) {
  const rows = [
    { icon: <Truck className="w-4 h-4" />, label: "DOT #", value: result.dot_number || "—" },
    { icon: <Truck className="w-4 h-4" />, label: "MC docket", value: result.docket_number || "—" },
    {
      icon: <MapPin className="w-4 h-4" />,
      label: "Address",
      value: [result.physical_address, result.city, result.state, result.zip].filter(Boolean).join(", ") || "—",
    },
    { icon: <Phone className="w-4 h-4" />, label: "Phone", value: result.phone || "—" },
    { icon: <Users className="w-4 h-4" />, label: "Drivers", value: result.drivers || 0 },
    { icon: <Truck className="w-4 h-4" />, label: "Power units", value: result.power_units || 0 },
    {
      icon: <CheckCircle2 className="w-4 h-4" />,
      label: "Operating status",
      value: result.operating_status || result.status_code,
    },
    {
      icon: <CheckCircle2 className="w-4 h-4" />,
      label: "Entity type",
      value: result.entity_type || "—",
    },
    {
      icon: <CheckCircle2 className="w-4 h-4" />,
      label: "Last MCS-150 filing",
      value: result.mcs150_date || "—",
    },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6">
      <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-4">
        FMCSA Record
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {rows.map((r) => (
          <div key={r.label} className="flex items-start gap-2 text-sm">
            <div className="mt-0.5 text-white/40 shrink-0">{r.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-0.5">{r.label}</div>
              <div className="text-white break-words">{r.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════ SAFETY CHECKLIST ═════════════════════════ */
function SafetyChecklist({ result }) {
  const items = [
    {
      pass: result.status_code === "A",
      label: "Active operating status",
      hint: "Currently authorized to operate",
    },
    {
      pass: result.docket_status === "A" && !!result.docket_number,
      label: "Valid MC docket",
      hint: "Insurance and authority filings up to date (basic level)",
    },
    {
      pass: !result.out_of_service,
      label: "Not currently out of service",
      hint: "No active OOS order from FMCSA",
    },
    {
      pass: String(result.entity_type || "").toUpperCase().includes("AUTHORIZED"),
      label: "Authorized for hire",
      hint: "Registered to contract loads (not just a private carrier)",
    },
    {
      pass: (result.power_units || 0) > 0,
      label: "Has registered power units",
      hint: "Has actual trucks on file with FMCSA",
    },
    {
      pass: !!result.mcs150_date,
      label: "Recent MCS-150 filing",
      hint: "Filed the biennial DOT update — a compliance basic",
    },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6">
      <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-4">
        Verification checklist
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.label} className="flex items-start gap-3">
            {it.pass ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className={`text-sm font-semibold ${it.pass ? "text-emerald-100" : "text-red-100"}`}>
                {it.label}
              </div>
              <div className="text-[12px] text-white/50">{it.hint}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-white/10 text-[12px] text-white/60 leading-snug">
        <b className="text-white/90">⚠️ Even a green checklist isn't a guarantee.</b> Before hauling, always: (1) verify insurance certificate matches broker's name, (2) check payment history on RMIS / Ansonia, (3) call the broker directly from the phone number on their FMCSA record, not one they text you.
      </div>
    </div>
  );
}

function BenefitCard({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6 hover:border-[#FF5A1F]/40 transition-colors">
      <div className="w-11 h-11 rounded-lg bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center mb-4">
        {icon}
      </div>
      <div className="text-lg font-bold mb-2">{title}</div>
      <div className="text-white/60 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
